import type { SimState } from '../core/sim.ts';
import type { ChatMessage, ChatScope } from '../core/chat.ts';
import { SNAPSHOT_HZ, type ClassId, type Faction } from '../core/constants.ts';
import type { RoomLink } from './roomTransport.ts';
import {
  fromSnapshot, readMessage, type ClientMessage, type ServerMessage, type Snapshot,
} from './protocol.ts';

/** Further than this between two snapshots is a teleport, drawn where it lands. */
const SNAP_DRAW = 4;

/**
 * A room at the other end of a socket.
 *
 * The same four methods the loopback offers, which is the whole reason the split
 * was worth doing: the client sends input and collects a view, and whether the
 * room is a function call away or across an ocean is not something anything
 * above this line has to know.
 *
 * Two things genuinely differ, and both are absences rather than additions.
 * There is no `advance`, because the room runs on its own clock and a client
 * cannot drive it. And there is no `filter`, because over a wire the visibility
 * rule is not a preference — it is the only thing standing between a modified
 * client and a wallhack, and it is applied at the far end where a client cannot
 * reach it.
 */

export interface Connecting {
  /** Resolves once the room has said which player you are. */
  ready: Promise<RoomLink>;
  /** The arena, which arrives once and is reused for the life of the match. */
  arena(): SimState['arena'] | null;
  /** Which map the room is playing, so the client can load its geometry. */
  mapId(): string;
  close(): void;
}

/**
 * How long to wait for a `welcome` before giving up.
 *
 * A socket that opens and then says nothing is the most confusing failure a
 * player can have — the game appears to be loading and never stops — so it is
 * given a deadline and turned into an error somebody can read.
 */
const WELCOME_TIMEOUT = 8000;

/**
 * A server address as a WebSocket URL, or an explanation of why it is not one.
 *
 * Three mistakes account for nearly every "could not reach the server", and none
 * of them look like anything from inside a browser: the `error` event a failed
 * WebSocket fires carries no detail at all, deliberately, so a client that only
 * reports "it failed" has told the person nothing they can act on.
 *
 * So the address is checked here, where the problem is still legible:
 *
 * **A missing scheme.** `VITE_SERVER_URL=my-game.up.railway.app` is the obvious
 * thing to type and produces a string `new WebSocket` rejects outright.
 *
 * **Mixed content.** A page served over HTTPS — which anything on Cloudflare
 * Pages is — may not open a `ws://` socket. The browser blocks it before a
 * packet moves and fires the same featureless error as a server that is simply
 * down. This is the one worth naming, because the fix is a one-character edit to
 * an environment variable and it is invisible otherwise.
 *
 * **A trailing path.** Harmless, but stripped so the socket and the lobby list
 * are talking to the same place.
 */
export function toWsUrl(server: string): { url: string } | { problem: string } {
  const trimmed = server.trim();
  if (!trimmed) return { problem: 'No server address is configured' };

  /*
   * Scheme first, and *before* any tidying of the string.
   *
   * Trailing slashes were being stripped ahead of this, which turned the
   * incomplete `http://` into `http:` — no longer scheme-shaped, so `https://`
   * was prefixed onto it and `https://http:` parses quite happily as the host
   * `http`. A nonsense address became a plausible-looking socket to a machine
   * called "http". `URL` already ignores a trailing slash, so there was nothing
   * to strip in the first place.
   *
   * A bare host is assumed secure rather than insecure: every deployment that
   * matters is behind TLS, and guessing the other way produces the mixed-content
   * refusal below.
   */
  const bare = !/^[a-z]+:\/\//i.test(trimmed);
  /*
   * A bare host is assumed secure — unless it is this machine.
   *
   * Every deployment that matters is behind TLS, so `https` is the right guess
   * for a hostname somebody pasted out of a dashboard, and guessing the other
   * way produces the mixed-content refusal below. But nothing on `localhost` has
   * a certificate, and a developer typing `localhost:8080` into `?server=` means
   * the plain one — assuming TLS there fails with a message about reaching
   * `https://localhost`, which is confusing precisely when somebody is trying to
   * get their first local server talking.
   */
  const host = trimmed.split(/[/:]/)[0]!.toLowerCase();
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
    || host.endsWith('.local');
  const withScheme = bare ? `${isLocal ? 'http' : 'https'}://${trimmed}` : trimmed;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return { problem: `"${server}" is not a valid address` };
  }
  if (!parsed.hostname) return { problem: `"${server}" is not a valid address` };

  const secure = parsed.protocol === 'https:' || parsed.protocol === 'wss:';
  const pageIsSecure = typeof location !== 'undefined' && location.protocol === 'https:';
  if (pageIsSecure && !secure) {
    return {
      problem:
        `This page is served over HTTPS, so it cannot open an insecure socket to `
        + `${parsed.host}. Set the server address to https:// rather than http:// `
        + '(VITE_SERVER_URL on the frontend).',
    };
  }

  return { url: `${secure ? 'wss:' : 'ws:'}//${parsed.host}` };
}

/** The same address as a plain HTTP origin, for the lobby list and for probing. */
export function toHttpUrl(server: string): string {
  const ws = toWsUrl(server);
  if ('problem' in ws) return '';
  return ws.url.replace(/^ws/, 'http');
}

/**
 * Whether the server answers at all, asked over plain HTTP.
 *
 * Used only to explain a socket that would not open. A `fetch` that succeeds
 * where a WebSocket failed is a very specific finding — the machine is up and
 * something between here and there is refusing the upgrade, which is a proxy or
 * a scheme problem rather than a dead server — and it is the difference between
 * a person checking their URL and a person redeploying for an hour.
 */
async function serverAnswers(server: string): Promise<boolean> {
  const http = toHttpUrl(server);
  if (!http) return false;
  try {
    const response = await fetch(`${http}/rooms`, { method: 'GET' });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Turns a dead socket into something a person can act on.
 *
 * The close code carries more than the error event does — 1008 is a refusal with
 * a reason attached, 1013 is a limiter, 1006 is "it never opened and nobody said
 * why", which is the one that needs the HTTP probe to tell apart.
 */
async function explain(
  server: string, code: number | null, reason: string,
): Promise<string> {
  if (reason) return reason;
  if (code === 1008) return 'The server refused the connection';
  if (code === 1013) return 'The server is busy or is refusing new connections';

  const up = await serverAnswers(server);
  if (up) {
    return `${toHttpUrl(server)} answers over HTTP but refused a WebSocket. `
      + 'Check that the host allows WebSocket upgrades.';
  }
  return `Could not reach ${toHttpUrl(server) || server}. `
    + 'Check the address, and that the server is running.';
}

/** What a host wants opened, when they are not joining something that exists. */
export interface NewRoom {
  name: string;
  mapId?: string;
  mapPool?: string[];
  carryXp?: boolean;
  maxPlayers?: number;
  xpScale?: number;
  roundSeconds?: number;
  bestOf?: number;
  bots?: { order?: number; balance?: number; chaos?: number };
}

/**
 * Joins a room, opening it first if asked to.
 *
 * Creating used to be a separate function with a socket of its own, which worked
 * and was worse: two connections against a per-address limit, two chances for a
 * proxy to refuse an upgrade, and two entirely separate failure paths to explain
 * to somebody whose lobby would not open. One socket does both — `create`, wait
 * for `opened`, then `join` the id it comes back with — and there is exactly one
 * place where a connection can go wrong.
 *
 * `server` is the address as configured rather than a URL, because working out
 * what it should be is part of the job: see `toWsUrl`.
 */
/**
 * Opens a room on the server without sitting down in it.
 *
 * Exists because a lobby is a *screen*, not a match: the host spends up to two
 * minutes in it picking a class and waiting for people, and until now the room
 * was not created until all of that had finished and the arena had loaded — so
 * a friend refreshing the join list saw nothing until the match had already
 * started, which is exactly too late to join it.
 *
 * A socket of its own, closed the moment the id comes back. That is a second
 * connection where `connect` needs only one, and it is the right trade: the
 * alternative is holding a socket open through the whole lobby wait for no
 * reason other than that it was opened early, and the server holds a newly
 * created room against the reaper precisely so this can close.
 */
export async function openLobby(server: string, room: NewRoom & { secret?: string }): Promise<string> {
  const address = toWsUrl(server);
  if ('problem' in address) throw new Error(address.problem);

  return new Promise<string>((resolve, reject) => {
    const socket = new WebSocket(address.url);
    let settled = false;
    const finish = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      socket.close();
      fn();
    };
    const timer = setTimeout(
      () => finish(() => reject(new Error('The server did not answer in time'))),
      WELCOME_TIMEOUT,
    );

    socket.addEventListener('open', () => {
      const message: ClientMessage = { t: 'create', ...room };
      socket.send(JSON.stringify(message));
    });
    socket.addEventListener('message', (event) => {
      const reply = readMessage<ServerMessage>(String(event.data));
      if (!reply) return;
      if (reply.t === 'opened') finish(() => resolve(reply.room));
      else if (reply.t === 'denied') finish(() => reject(new Error(reply.why)));
    });
    socket.addEventListener('close', (ev) => {
      const code = (ev as CloseEvent).code ?? null;
      const reason = (ev as CloseEvent).reason ?? '';
      if (settled) return;
      void explain(server, code, reason).then((why) => finish(() => reject(new Error(why))));
    });
    socket.addEventListener('error', () => {
      void explain(server, null, '').then((why) => finish(() => reject(new Error(why))));
    });
  });
}

export function connect(
  server: string,
  room: string | { create: NewRoom },
  who: {
    name: string;
    faction: Faction;
    cls: ClassId;
    loadout?: string[];
    /** The shared key, when the server is asking for one. */
    secret?: string;
    /** Bots to seat, honoured only when this is the first arrival. */
    bots?: { order?: number; balance?: number; chaos?: number };
  },
): Connecting {
  const address = toWsUrl(server);
  if ('problem' in address) {
    /*
     * Refused before a socket is opened, and refused *legibly*.
     *
     * A misconfigured address is not a network failure and should not be
     * reported as one — the person has a variable to fix, and telling them the
     * server is unreachable sends them to look at the server.
     */
    return {
      ready: Promise.reject(new Error(address.problem)),
      arena: () => null,
      mapId: () => 'great-cathedral',
      close: () => {},
    };
  }

  const socket = new WebSocket(address.url);
  /** Set by the close handler so the failure can say what happened. */
  let closeCode: number | null = null;
  let closeReason = '';

  let arena: SimState['arena'] | null = null;
  let mapId = 'great-cathedral';
  let playerId = '';

  /*
   * The latest picture, held so `collect` can answer at once.
   *
   * A `RoomLink` is synchronous — the renderer asks for the world in the middle
   * of a frame and cannot wait — while a socket is not. So arriving snapshots
   * are kept here and handed over whenever they are next asked for, which is
   * exactly the arrangement every networked game uses and the reason the
   * interpolation work had to come first.
   */
  let latest: SimState | null = null;
  /** When the latest snapshot landed, in `performance.now()` milliseconds. */
  let arrivedAt = 0;
  /** The last input the room said it had processed, for prediction to replay from. */
  let ack = -1;
  let seq = 0;
  let events: ServerMessage extends { events: infer E } ? E : never[] = [] as never;
  let chat: ChatMessage[] = [];

  const speak = (message: ClientMessage): void => {
    if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  };

  const ready = new Promise<RoomLink>((resolve, reject) => {
    // One answer only: a refusal that also closes the socket would otherwise
    // reject twice, and a resolved connection that later drops must not reject
    // a promise somebody has already acted on.
    let settled = false;
    /**
     * The link, built on `welcome` and handed over on the first snapshot.
     *
     * Two messages make a usable connection and they arrive separately, so the
     * thing being promised exists for a moment before it is worth giving out.
     */
    let seated: RoomLink | null = null;
    const giveUp = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(
        'The server accepted the connection but never answered. '
        + 'It may be running an older build that does not know how to open a room.',
      ));
    }, WELCOME_TIMEOUT);

    socket.addEventListener('open', () => {
      // A host opens the room first and joins whatever it turns out to be
      // called; anybody else joins straight away.
      if (typeof room === 'string') speak({ t: 'join', room, ...who, bots: who.bots });
      else speak({ t: 'create', ...room.create, secret: who.secret });
    });

    /*
     * A close is an answer too, and usually a better one than the error event.
     *
     * `error` in a browser carries no detail at all — deliberately — so a client
     * that only listens for it can say nothing more useful than "it failed".
     * The close code says whether the server refused (1008), was busy (1013), or
     * never accepted the socket in the first place (1006), and the last of those
     * is worth an HTTP probe before anybody is told to go and check their server.
     */
    socket.addEventListener('close', (ev) => {
      closeCode = (ev as CloseEvent).code ?? null;
      closeReason = (ev as CloseEvent).reason ?? '';
      if (settled) return;
      settled = true;
      clearTimeout(giveUp);
      void explain(server, closeCode, closeReason).then((why) => reject(new Error(why)));
    });

    socket.addEventListener('error', () => {
      if (settled) return;
      settled = true;
      clearTimeout(giveUp);
      void explain(server, closeCode, closeReason).then((why) => reject(new Error(why)));
    });

    socket.addEventListener('message', (ev) => {
      const message = readMessage<ServerMessage>(
        typeof ev.data === 'string' ? ev.data : '',
      );
      if (!message) return;

      switch (message.t) {
        /*
         * The room exists; now sit down in it.
         *
         * The id is the server's choice rather than the host's — a client that
         * named its own room could collide with one that already exists — so the
         * join has to wait for this rather than guessing what it will be called.
         */
        case 'opened': {
          speak({ t: 'join', room: message.room, ...who });
          break;
        }

        case 'welcome': {
          arena = message.arena as SimState['arena'];
          mapId = message.mapId;
          playerId = message.playerId;

          /*
           * Built here, handed over when there is a world to hand over.
           *
           * `welcome` says the seat is yours; it does not carry a state. The
           * first snapshot does, and until it lands there is nothing for
           * `collect` to return — which used to be papered over with an empty
           * object cast to a `SimState`, a lie that every caller downstream
           * believed. The first one to look past `players` found no `match` on
           * it and threw, which is a failure at the start of a match with a
           * message about a property.
           *
           * So the promise waits for a real state. One tick on the server,
           * inside the handshake timeout that is already running, and after it
           * nobody has to wonder whether a link is furnished.
           */
          seated = {
            playerId,
            // Stamped on the way out. The room hands the number back untouched
            // and the client uses it to know what still needs replaying.
            // The prediction's number when there is one, so the room acknowledges
            // inputs by the count the prediction keeps. See `RoomTransport.pump`.
            send: (input) => {
              const { seq: stamped, ...bare } = input;
              speak({ t: 'input', input: bare, seq: typeof stamped === 'number' ? stamped : seq++ });
            },
            say: (scope: ChatScope, text: string) => speak({ t: 'chat', scope, text }),
            // Lobby only; the room ignores both once a round is running.
            setReady: (on: boolean) => speak({ t: 'ready', on }),
            addBot: (faction) => speak({ t: 'addBot', faction }),
            rename: (name: string) => speak({ t: 'rename', name }),
            reseat: (faction, cls, loadout) => speak({ t: 'reseat', faction, cls, loadout }),
            collect: () => {
              const taken = { events, chat };
              events = [] as never;
              chat = [];
              return {
                // Never null: the handshake does not complete until a snapshot
                // has landed, so a link that exists has a world behind it.
                state: latest as SimState,
                events: taken.events as never,
                chat: taken.chat,
                /*
                 * How far through the interval since the last snapshot. Drawn
                 * between that snapshot and the one before (see the snapshot
                 * handler), so this reaches 1 just as the next one is due.
                 */
                alpha: Math.min(1, (performance.now() - arrivedAt) / (1000 / SNAPSHOT_HZ)),
                ack,
              };
            },
          };
          break;
        }

        case 'denied':
          // A refusal with a reason is the best failure there is. Marked settled
          // so the close that follows it does not overwrite it with a guess.
          settled = true;
          clearTimeout(giveUp);
          reject(new Error(message.why));
          break;

        case 'snapshot': {
          if (!arena) break;
          {
            const previous = latest;
            latest = fromSnapshot(message.state as Snapshot, arena);
            /*
             * Everybody is drawn from where the *previous snapshot* had them to
             * where this one does, over one snapshot interval — so other mages,
             * twenty updates a second on the wire, move at the screen's rate
             * instead of in twenty jumps a second. That costs one interval of
             * delay on how other people are drawn, which every networked shooter
             * pays for exactly this. Your own mage is the prediction's business
             * and is replaced before anything draws it.
             */
            const before = new Map(previous?.players.map((p) => [p.id, p.pos]) ?? []);
            for (const p of latest.players) {
              const was = before.get(p.id);
              // A teleport — a respawn, a Call — is drawn where it lands, not
              // streaked across the map in a twentieth of a second.
              p.prevPos = was && Math.hypot(was.x - p.pos.x, was.y - p.pos.y, was.z - p.pos.z) < SNAP_DRAW
                ? { ...was }
                : { ...p.pos };
            }
            arrivedAt = performance.now();
          }
          // The first one completes the handshake. See `welcome`.
          if (seated && !settled) {
            settled = true;
            clearTimeout(giveUp);
            resolve(seated);
          }
          ack = message.ack;
          events = [...(events as never[]), ...message.events] as never;
          chat = [...chat, ...message.chat];
          break;
        }

        default:
          break;
      }
    });

  });

  return {
    ready,
    arena: () => arena,
    mapId: () => mapId,
    close: () => {
      speak({ t: 'leave' });
      socket.close();
    },
  };
}

/** Asks a server what rooms it has, before there is any socket at all. */
export async function fetchLobbies(base: string): Promise<unknown[]> {
  // Normalised the same way the socket address is, so the list and the room a
  // player then joins cannot end up pointing at two different places.
  const origin = toHttpUrl(base);
  if (!origin) return [];
  const response = await fetch(`${origin}/rooms`);
  if (!response.ok) return [];
  return (await response.json()) as unknown[];
}
